import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import favicon from "../assets/favicon_new.png";

ChartJS.register(ArcElement, Tooltip, Legend);

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

const formatFilterAppliedLabel = (filterApplied) => {
  if (!filterApplied) return "Unknown";

  switch (filterApplied.type) {
    case "current_month":
      return "Current Month";
    case "specific_month":
      return `${MONTHS[(filterApplied.month || 1) - 1]} ${filterApplied.year}`;
    case "date_range":
      return `${filterApplied.start_date} to ${filterApplied.end_date}`;
    case "all":
      return "All Time";
    default:
      return filterApplied.type;
  }
};

const buildUsageExportFilename = (filterApplied, summary) => {
  const from = summary?.date_range?.from;
  const to = summary?.date_range?.to;

  if (from && to) {
    return `usage_${from}_to_${to}.xlsx`;
  }

  if (
    filterApplied?.type === "date_range" &&
    filterApplied.start_date &&
    filterApplied.end_date
  ) {
    return `usage_${filterApplied.start_date}_to_${filterApplied.end_date}.xlsx`;
  }

  return `usage_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
};

const UsageSection = ({ isDarkMode, themeClasses, setActiveSection }) => {
  const [usageStats, setUsageStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // Filter state
  const [filterType, setFilterType] = useState("current_month");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isExportingUsageExcel, setIsExportingUsageExcel] = useState(false);

  const fetchUsageStats = useCallback(async (params = {}) => {
    try {
      setStatsError(null);
      setStatsLoading(true);
      const token = localStorage.getItem("authToken");

      const queryParams = new URLSearchParams();
      queryParams.set("filter", params.filterType || filterType);
      queryParams.set("sort_by", params.sortBy || sortBy);
      queryParams.set("sort_order", params.sortOrder || sortOrder);

      if ((params.filterType || filterType) === "specific_month") {
        queryParams.set("year", params.selectedYear || selectedYear);
        queryParams.set("month", params.selectedMonth || selectedMonth);
      }
      if ((params.filterType || filterType) === "date_range") {
        queryParams.set("start_date", params.startDate || startDate);
        queryParams.set("end_date", params.endDate || endDate);
      }

      const response = await axios.get(`/api/usage_stats?${queryParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsageStats(response.data);
    } catch (error) {
      console.error("Error fetching usage stats:", error);
      setStatsError(error.response?.data?.error || error.message);
    } finally {
      setStatsLoading(false);
    }
  }, [filterType, sortBy, sortOrder, selectedYear, selectedMonth, startDate, endDate]);

  useEffect(() => {
    fetchUsageStats();
  }, []);

  const handleApplyFilter = () => {
    if (filterType === "date_range" && (!startDate || !endDate)) return;
    fetchUsageStats();
  };

  const exportUsageToExcel = useCallback(() => {
    if (!usageStats?.usage_details?.length) return;

    try {
      setIsExportingUsageExcel(true);

      const { filter_applied, summary, limits, usage_details } = usageStats;
      const filterLabel = formatFilterAppliedLabel(filter_applied);
      const sortLabel = `${filter_applied?.sort_by || sortBy} (${filter_applied?.sort_order || sortOrder})`;
      const dailyLimitValue = limits?.daily_limit || 10;

      const summarySheetData = [
        ["Filter Applied", filterLabel],
        ["Sort By", sortLabel],
        ["Total Usage", summary?.total_usage ?? 0],
        ["Total Days", summary?.total_days ?? 0],
        ["Average Daily", summary?.average_daily ?? 0],
        ["Date Range From", summary?.date_range?.from ?? ""],
        ["Date Range To", summary?.date_range?.to ?? ""],
        ["Daily Limit", dailyLimitValue],
        ["Monthly Limit", limits?.monthly_limit ?? 50],
      ];

      const breakdownSheetData = [
        ["Filter Applied", filterLabel],
        [],
        ["Date", "Day", "Scans", "% of Daily Limit"],
        ...usage_details.map((record) => [
          record.date,
          record.day_of_week,
          record.count,
          dailyLimitValue > 0 ? Math.round((record.count / dailyLimitValue) * 100) : 0,
        ]),
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(summarySheetData),
        "Summary"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(breakdownSheetData),
        "Daily Breakdown"
      );

      XLSX.writeFile(workbook, buildUsageExportFilename(filter_applied, summary));
      toast.success("Usage data exported to Excel successfully.");
    } catch (error) {
      console.error("Error exporting usage to Excel:", error);
      toast.error("Failed to export usage to Excel.");
    } finally {
      setIsExportingUsageExcel(false);
    }
  }, [usageStats, sortBy, sortOrder]);

  const hasExportData = (usageStats?.usage_details?.length ?? 0) > 0;

  const exportButtonBaseClassName = `rounded-lg text-sm font-medium transition-colors ${
    isExportingUsageExcel || !hasExportData
      ? "opacity-50 cursor-not-allowed"
      : isDarkMode
      ? "bg-green-600/30 text-green-300 hover:bg-green-500/40 border border-green-500/30"
      : "bg-green-100 text-green-700 hover:bg-green-200 border border-green-300"
  }`;

  const renderExportButton = (compact = false) => (
    <button
      type="button"
      onClick={exportUsageToExcel}
      disabled={isExportingUsageExcel || !hasExportData}
      title={!hasExportData ? "No data to export" : undefined}
      className={`${exportButtonBaseClassName} ${compact ? "px-3 py-1.5" : "px-4 py-2"}`}
    >
      {isExportingUsageExcel ? "Exporting..." : "Export Excel"}
    </button>
  );

  // Derive today & monthly values from new response shape
  const todayStr = new Date().toISOString().split("T")[0];
  const todayRecord = usageStats?.usage_details?.find((d) => d.date === todayStr);
  const todayCount = todayRecord?.count || 0;
  const dailyLimit = usageStats?.limits?.daily_limit || 10;
  const todayRemaining = Math.max(0, dailyLimit - todayCount);

  const monthTotal = usageStats?.summary?.total_usage || 0;
  const monthlyLimit = usageStats?.limits?.monthly_limit || 50;
  const monthRemaining = Math.max(0, monthlyLimit - monthTotal);

  const createChartData = (used, remaining, isDaily = true) => ({
    labels: ["Used", "Remaining"],
    datasets: [
      {
        data: [used, remaining],
        backgroundColor: isDarkMode
          ? [
              isDaily ? "rgba(56, 189, 248, 0.3)" : "rgba(168, 85, 247, 0.3)",
              "rgba(31, 41, 55, 0.5)",
            ]
          : [
              isDaily ? "rgba(56, 189, 248, 0.5)" : "rgba(168, 85, 247, 0.5)",
              "rgba(243, 244, 246, 0.5)",
            ],
        borderColor: isDarkMode
          ? [
              isDaily ? "rgba(56, 189, 248, 0.8)" : "rgba(168, 85, 247, 0.8)",
              "rgba(75, 85, 99, 0.3)",
            ]
          : [
              isDaily ? "rgba(56, 189, 248, 1)" : "rgba(168, 85, 247, 1)",
              "rgba(229, 231, 235, 1)",
            ],
        borderWidth: 2,
      },
    ],
  });

  const pieOptions = (isDark) => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: isDark ? "#1f2937" : "#ffffff",
        titleColor: isDark ? "#f9fafb" : "#111827",
        bodyColor: isDark ? "#d1d5db" : "#374151",
        borderColor: isDark ? "#374151" : "#e5e7eb",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
      },
    },
    cutout: "75%",
    animation: { animateRotate: true, duration: 1500, easing: "easeOutQuart" },
  });

  const inputCls = `px-3 py-1.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
    isDarkMode
      ? "bg-gray-800 border-gray-700 text-gray-200"
      : "bg-white border-gray-200 text-gray-800"
  }`;

  const labelCls = `text-xs font-medium mb-1 block ${themeClasses.subtext}`;

  if (statsLoading) {
    return (
      <div className="animate-fadeIn flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-7 h-7 mx-auto mb-4">
            <motion.div
              className="w-full h-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 2, ease: "linear", repeat: Infinity }}
            >
              <img
                src={favicon}
                alt="Loading"
                className="w-full h-full object-contain"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </motion.div>
          </div>
          <p className={themeClasses.text}>Loading usage stats...</p>
        </div>
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="animate-fadeIn">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500">Error loading stats: {statsError}</p>
            <button
              onClick={() => fetchUsageStats()}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6">

      {/* ── Filter Controls ───────────────────────────────────────── */}
      <div
        className={`p-4 rounded-xl border ${
          isDarkMode
            ? "bg-white/5 border-slate-800"
            : "bg-white border-gray-200 shadow-sm"
        }`}
      >
        <h3 className={`text-sm font-semibold mb-4 ${themeClasses.text}`}>
          Filter Usage Data
        </h3>

        <div className="flex flex-wrap gap-4 items-end">
          {/* Filter Type */}
          <div>
            <label className={labelCls}>Filter By</label>
            <select
              className={inputCls}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="current_month">Current Month</option>
              <option value="specific_month">Specific Month</option>
              <option value="date_range">Date Range</option>
              <option value="all">All Time</option>
            </select>
          </div>

          {/* Specific Month pickers */}
          {filterType === "specific_month" && (
            <>
              <div>
                <label className={labelCls}>Month</label>
                <select
                  className={inputCls}
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Year</label>
                <select
                  className={inputCls}
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Date Range pickers */}
          {filterType === "date_range" && (
            <>
              <div>
                <label className={labelCls}>From</label>
                <input
                  type="date"
                  className={inputCls}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>To</label>
                <input
                  type="date"
                  className={inputCls}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </>
          )}

          {/* Sort controls */}
          <div>
            <label className={labelCls}>Sort By</label>
            <select
              className={inputCls}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date">Date</option>
              <option value="count">Usage Count</option>
            </select>
          </div>

          <div>
            <label className={labelCls}>Order</label>
            <select
              className={inputCls}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>

          {/* Apply */}
          <button
            onClick={handleApplyFilter}
            disabled={filterType === "date_range" && (!startDate || !endDate)}
            className="px-5 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Apply
          </button>

          {renderExportButton(true)}
        </div>

        {/* Active filter summary chip */}
        {usageStats?.filter_applied && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                isDarkMode ? "bg-blue-500/20 text-blue-300" : "bg-blue-50 text-blue-700 border border-blue-100"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
              {usageStats.filter_applied.type === "current_month" && "Current Month"}
              {usageStats.filter_applied.type === "specific_month" &&
                `${MONTHS[(usageStats.filter_applied.month || 1) - 1]} ${usageStats.filter_applied.year}`}
              {usageStats.filter_applied.type === "date_range" &&
                `${usageStats.filter_applied.start_date} → ${usageStats.filter_applied.end_date}`}
              {usageStats.filter_applied.type === "all" && "All Time"}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
              }`}
            >
              Sorted by {usageStats.filter_applied.sort_by} ({usageStats.filter_applied.sort_order})
            </span>
          </div>
        )}
      </div>

      {/* ── Summary Cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Today's Usage */}
        <div
          className={`group p-4 rounded-xl ${
            isDarkMode
              ? "bg-white/5 border border-slate-950 backdrop-blur-xl"
              : "bg-white border border-slate-280 shadow-sm hover:shadow-lg"
          } transition-all duration-500 hover:scale-[1.01] transform-gpu`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${isDarkMode ? "bg-blue-500/20" : "bg-blue-50"}`}>
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className={`text-lg font-bold tracking-tight ${themeClasses.text}`}>Today's Usage</h3>
            </div>
            <div className={`px-3 py-1.5 rounded-full font-semibold text-xs ${
              isDarkMode ? "bg-blue-500/20 text-blue-400" : "bg-blue-50 text-blue-700 border border-blue-100"
            }`}>
              {dailyLimit > 0 ? Math.round((todayCount / dailyLimit) * 100) : 0}%
            </div>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-4">
            <div className="relative w-32 h-32 flex-shrink-0">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/10 to-blue-600/10 animate-pulse" />
              <Pie
                data={createChartData(todayCount, todayRemaining, true)}
                options={pieOptions(isDarkMode)}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className={`text-xl font-bold ${themeClasses.text}`}>
                    {dailyLimit > 0 ? Math.round((todayCount / dailyLimit) * 100) : 0}%
                  </p>
                  <p className={`text-xs font-medium ${themeClasses.subtext}`}>Used</p>
                </div>
              </div>
            </div>

            <div className="flex-1 w-full space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className={`group p-3 rounded-lg transition-all duration-300 ${
                  isDarkMode ? "bg-blue-500/10 border border-slate-800" : "bg-blue-50 border border-slate-200"
                }`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                    <p className="text-sm font-semibold text-blue-600">Scanned</p>
                  </div>
                  <p className="text-xl font-bold text-blue-700 tracking-tight">{todayCount.toLocaleString()}</p>
                </div>
                <div className={`group p-3 rounded-lg border transition-all duration-300 ${
                  isDarkMode ? "bg-green-500/10 border-slate-800" : "bg-green-50 border-slate-200"
                }`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                    <p className="text-sm font-semibold text-green-600">Remaining</p>
                  </div>
                  <p className="text-xl font-bold text-green-700 tracking-tight">{todayRemaining.toLocaleString()}</p>
                </div>
              </div>
              <div className={`p-3 rounded-lg border-2 border-dashed ${
                isDarkMode ? "bg-gray-800/30 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${themeClasses.subtext} mb-0.5`}>Daily Limit</p>
                    <p className={`text-lg font-bold ${themeClasses.text} tracking-tight`}>{dailyLimit.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Period Summary */}
        <div
          className={`group p-4 rounded-xl border ${
            isDarkMode
              ? "bg-white/5 border-slate-950 backdrop-blur-xl"
              : "bg-white border-slate-280 shadow-sm hover:shadow-lg"
          } transition-all duration-500 hover:scale-[1.01] transform-gpu`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${isDarkMode ? "bg-purple-500/20" : "bg-purple-50"}`}>
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className={`text-lg font-bold tracking-tight ${themeClasses.text}`}>
                {filterType === "current_month" ? "Monthly Usage" : "Period Summary"}
              </h3>
            </div>
            <div className={`px-3 py-1.5 rounded-full font-semibold text-xs ${
              isDarkMode ? "bg-purple-500/20 text-purple-400" : "bg-purple-50 text-purple-700 border border-purple-100"
            }`}>
              {monthlyLimit > 0 ? Math.round((monthTotal / monthlyLimit) * 100) : 0}%
            </div>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-4">
            <div className="relative w-32 h-32 flex-shrink-0">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500/10 to-purple-600/10 animate-pulse" />
              <Pie
                data={createChartData(monthTotal, Math.max(0, monthRemaining), false)}
                options={pieOptions(isDarkMode)}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className={`text-xl font-bold ${themeClasses.text}`}>
                    {monthlyLimit > 0 ? Math.round((monthTotal / monthlyLimit) * 100) : 0}%
                  </p>
                  <p className={`text-xs font-medium ${themeClasses.subtext}`}>Used</p>
                </div>
              </div>
            </div>

            <div className="flex-1 w-full space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className={`group p-3 rounded-lg border transition-all duration-300 ${
                  isDarkMode ? "bg-purple-500/10 border-slate-800" : "bg-purple-50 border-slate-200"
                }`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse" />
                    <p className="text-sm font-semibold text-purple-600">Total Used</p>
                  </div>
                  <p className="text-xl font-bold text-purple-700 tracking-tight">{monthTotal.toLocaleString()}</p>
                </div>
                <div className={`group p-3 rounded-lg border transition-all duration-300 ${
                  isDarkMode ? "bg-pink-500/10 border-slate-800" : "bg-pink-50 border-slate-200"
                }`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-1.5 h-1.5 bg-pink-500 rounded-full animate-pulse" />
                    <p className="text-sm font-semibold text-pink-600">Remaining</p>
                  </div>
                  <p className="text-xl font-bold text-pink-700 tracking-tight">{monthRemaining.toLocaleString()}</p>
                </div>
              </div>

              {/* Extra summary stats */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`p-3 rounded-lg border-2 border-dashed ${
                  isDarkMode ? "bg-gray-800/30 border-gray-700" : "bg-gray-50 border-gray-200"
                }`}>
                  <p className={`text-xs font-medium ${themeClasses.subtext} mb-0.5`}>Monthly Limit</p>
                  <p className={`text-lg font-bold ${themeClasses.text} tracking-tight`}>{monthlyLimit.toLocaleString()}</p>
                </div>
                <div className={`p-3 rounded-lg border-2 border-dashed ${
                  isDarkMode ? "bg-gray-800/30 border-gray-700" : "bg-gray-50 border-gray-200"
                }`}>
                  <p className={`text-xs font-medium ${themeClasses.subtext} mb-0.5`}>Avg / Day</p>
                  <p className={`text-lg font-bold ${themeClasses.text} tracking-tight`}>
                    {usageStats?.summary?.average_daily ?? 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Usage History Table ───────────────────────────────────── */}
      {usageStats?.usage_details?.length > 0 && (
        <div
          className={`p-4 rounded-xl border ${
            isDarkMode
              ? "bg-white/5 border-slate-800"
              : "bg-white border-gray-200 shadow-sm"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className={`text-sm font-semibold ${themeClasses.text}`}>
              Daily Breakdown
              <span className={`ml-2 text-xs font-normal ${themeClasses.subtext}`}>
                ({usageStats.summary.total_days} day{usageStats.summary.total_days !== 1 ? "s" : ""})
              </span>
            </h3>
            <div className="flex flex-wrap items-center gap-3">
              {usageStats.summary.date_range?.from && (
                <span className={`text-xs ${themeClasses.subtext}`}>
                  {usageStats.summary.date_range.from} → {usageStats.summary.date_range.to}
                </span>
              )}
              {renderExportButton()}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}>
                  <th className={`text-left py-2 px-3 text-xs font-semibold ${themeClasses.subtext}`}>Date</th>
                  <th className={`text-left py-2 px-3 text-xs font-semibold ${themeClasses.subtext}`}>Day</th>
                  <th className={`text-right py-2 px-3 text-xs font-semibold ${themeClasses.subtext}`}>Scans</th>
                  <th className={`text-right py-2 px-3 text-xs font-semibold ${themeClasses.subtext}`}>% of Daily Limit</th>
                </tr>
              </thead>
              <tbody>
                {usageStats.usage_details.map((record) => {
                  const pct = dailyLimit > 0 ? Math.round((record.count / dailyLimit) * 100) : 0;
                  const isToday = record.date === todayStr;
                  return (
                    <tr
                      key={record.date}
                      className={`border-b transition-colors ${
                        isDarkMode
                          ? `border-gray-800 ${isToday ? "bg-blue-500/10" : "hover:bg-white/5"}`
                          : `border-gray-50 ${isToday ? "bg-blue-50" : "hover:bg-gray-50"}`
                      }`}
                    >
                      <td className={`py-2.5 px-3 font-medium ${themeClasses.text}`}>
                        {record.date}
                        {isToday && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">Today</span>
                        )}
                      </td>
                      <td className={`py-2.5 px-3 ${themeClasses.subtext}`}>{record.day_of_week}</td>
                      <td className={`py-2.5 px-3 text-right font-semibold ${themeClasses.text}`}>
                        {record.count.toLocaleString()}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center justify-end gap-2">
                          <div className={`w-20 h-1.5 rounded-full overflow-hidden ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                            <div
                              className={`h-full rounded-full transition-all ${
                                pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-yellow-500" : "bg-blue-500"
                              }`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                          <span className={`text-xs font-medium w-8 text-right ${themeClasses.subtext}`}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {usageStats?.usage_details?.length === 0 && (
        <div className={`p-8 rounded-xl border text-center ${
          isDarkMode ? "bg-white/5 border-slate-800" : "bg-white border-gray-200 shadow-sm"
        }`}>
          <p className={`${themeClasses.subtext} text-sm`}>No usage data found for the selected period.</p>
        </div>
      )}

      {/* ── Upgrade CTA ───────────────────────────────────────────── */}
      <div className="flex justify-center items-center w-full mt-2 mb-4">
        <button
          onClick={() => setActiveSection("billing")}
          className="group justify-center items-center relative px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105 transition-all duration-300 ease-out"
        >
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>Upgrade Plan</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default UsageSection;
